import { useState } from 'react'
import { IonContent, IonPage, IonHeader, IonToolbar, IonTitle, IonButton, IonInput, IonText, useIonRouter } from '@ionic/react'
import { useMutation } from '@apollo/client/react'
import { UPDATE_USER_ACCOUNT } from '../graphql/mutations/updateUserAccount'
import { useUserAccount } from '../hooks/useUserAccount'
import type { UpdateUserAccountMutation, UpdateUserAccountMutationVariables } from '../types/graphql-generated'

export const CompleteProfilePage = () => {
  const router = useIonRouter()
  const { userAccount, refetch } = useUserAccount()
  const [firstName, setFirstName] = useState<string>(userAccount?.firstName || '')
  const [lastName, setLastName] = useState<string>(userAccount?.lastName || '')
  const [fullName, setFullName] = useState<string>(userAccount?.fullName || '')
  const [errors, setErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)

  const [updateUserAccount] = useMutation<UpdateUserAccountMutation, UpdateUserAccountMutationVariables>(
    UPDATE_USER_ACCOUNT
  )

  const isValid = firstName.trim() !== '' && lastName.trim() !== '' && fullName.trim() !== ''

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setErrors([])

    const validationErrors: string[] = []
    if (!firstName.trim()) validationErrors.push('El nombre es obligatorio')
    if (!lastName.trim()) validationErrors.push('El apellido es obligatorio')
    if (!fullName.trim()) validationErrors.push('El nombre a mostrar es obligatorio')
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    setSubmitting(true)
    try {
      const result = await updateUserAccount({
        variables: {
          input: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            fullName: fullName.trim(),
          },
        },
      })

      if (result.data?.updateUserAccount?.errors && result.data.updateUserAccount.errors.length > 0) {
        setErrors(result.data.updateUserAccount.errors)
        return
      }

      // Verify profile is complete from mutation result
      if (!result.data?.updateUserAccount?.userAccount?.fullName) {
        setErrors(['No se ha podido completar el perfil. Por favor, asegúrate de que todos los campos obligatorios estén cubiertos'])
        return
      }

      // Explicitly refetch the user account to ensure context is updated
      // This guarantees ProfileGuard will see the updated data
      await refetch()

      // Navigate using replace to prevent going back to this page
      router.push('/app/main', 'root', 'replace')
    } catch (error) {
      console.error('Error updating user account:', error)
      setErrors(['Ha ocurrido un error actualizando tu perfil. Por favor, inténtalo de nuevo'])
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Completa tu perfil</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="max-w-md mx-auto">
          <h1 className="text-2xl font-bold mb-4">Bienvenido a Futyclub!</h1>
          <p className="mb-6 text-gray-600">Por favor, completa tu perfil para continuar</p>

          <form onSubmit={handleSubmit}>
            <IonInput
              className="mb-4"
              type="text"
              label="Nombre *"
              labelPlacement="floating"
              fill="outline"
              value={firstName}
              onIonInput={(e) => setFirstName(e.detail.value || '')}
              placeholder="Introduce tu nombre"
            />

            <IonInput
              className="mb-4"
              type="text"
              label="Apellido *"
              labelPlacement="floating"
              fill="outline"
              value={lastName}
              onIonInput={(e) => setLastName(e.detail.value || '')}
              placeholder="Introduce tu apellido"
            />

            <IonInput
              className="mb-4"
              type="text"
              label="Nombre a mostrar *"
              labelPlacement="floating"
              fill="outline"
              value={fullName}
              onIonInput={(e) => setFullName(e.detail.value || '')}
              placeholder="Introduce el nombre que se mostrará a otros usuarios"
            />

            {errors.length > 0 && (
              <div className="mb-4">
                {errors.map((error, index) => (
                  <IonText color="danger" key={index}>
                    <p className="text-sm">{error}</p>
                  </IonText>
                ))}
              </div>
            )}

            <IonButton
              expand="block"
              type="submit"
              disabled={!isValid || submitting}
              className="mt-6"
            >
              {submitting ? 'Guardando...' : 'Continuar'}
            </IonButton>
          </form>
        </div>
      </IonContent>
    </IonPage>
  )
}
